import { PUBLIC_RPCS } from './constants.js'

/**
 * Proxy read-only JSON-RPC methods to a public node for the requested
 * chain. WC v2's UniversalProvider routes ALL `eth_*` calls through the
 * wallet by default — viem-based dApps fire `eth_estimateGas`,
 * `eth_call`, `eth_getBalance` etc. as part of building any tx, and if
 * we don't handle them the namespace layer rejects them and the dApp
 * surfaces "Unknown RPC error" with no useful detail.
 *
 * Safe{Wallet} advertises the same set of methods (see
 * `safe-wallet-monorepo/apps/web/src/features/walletconnect/services/utils.ts`)
 * — it routes them via @web3-onboard, we just hit a public RPC directly.
 */

export async function proxyRpc(
  eip155ChainId: string,
  method: string,
  params: unknown[],
): Promise<unknown> {
  const chainNum = parseChainId(eip155ChainId)
  const url = PUBLIC_RPCS[chainNum]
  if (!url) {
    throw new Error(
      `safeward has no public RPC configured for chain ${chainNum}; supported: ${Object.keys(PUBLIC_RPCS).join(', ')}`,
    )
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  })
  if (!resp.ok) {
    throw new Error(`Public RPC ${url} returned ${resp.status}`)
  }
  const json = (await resp.json()) as {
    result?: unknown
    error?: { code: number; message: string }
  }
  if (json.error) {
    const err = new Error(json.error.message) as Error & { code?: number }
    err.code = json.error.code
    throw err
  }
  return json.result
}

function parseChainId(eip155ChainId: string): number {
  // Accepts both "eip155:1" and "0x1" forms.
  if (eip155ChainId.startsWith('eip155:')) {
    return Number(eip155ChainId.slice('eip155:'.length))
  }
  if (eip155ChainId.startsWith('0x')) {
    return parseInt(eip155ChainId, 16)
  }
  return Number(eip155ChainId)
}
